import { CalendarDaysIcon, ClockIcon, UserIcon } from '@heroicons/react/24/solid';
import type { DateTimeSerializable } from '@vs-shop/schemas/DateTimeSerializable';

import {
  ArticleCardMeta,
  ArticleCardMetaAuthor,
  ArticleCardMetaDate,
  ArticleCardMetaReadTime,
  ArticleCardMetaValue,
} from '@vs-shop/commerce-ui/articleCard';

import { getMemoizedIntlShapeWithDefaultLocale } from '../../../../utils/intl';

import { IntlDate, IntlDecimal } from '../../intl/Intl';

import type {
  ArticleAuthorIndex,
  ArticleAuthorIndexFavourite,
} from '../../../../effect/schema/openapi/backend';

type Props = {
  publishedAt: DateTimeSerializable | null;
  author: ArticleAuthorIndex | ArticleAuthorIndexFavourite | null;
  readTime?: number | null;
};

/**
 * The facts row a card carries under its headline. The marks are this tenant's
 * drawing — heroicons, the set the package already ships — and the shared parts
 * state how big they render, so no icon library crosses into
 * `@vs-shop/commerce-ui`.
 */
export function ArticleInfoHeader({ publishedAt, author, readTime }: Props) {
  const intl = getMemoizedIntlShapeWithDefaultLocale();

  return (
    <ArticleCardMeta>
      {publishedAt !== null && (
        <ArticleCardMetaDate>
          <CalendarDaysIcon />
          <ArticleCardMetaValue>
            <IntlDate value={publishedAt} month="long" />
          </ArticleCardMetaValue>
        </ArticleCardMetaDate>
      )}

      {author !== null && (
        <ArticleCardMetaAuthor href={author.routerUrl}>
          <UserIcon />
          <ArticleCardMetaValue>{author.name}</ArticleCardMetaValue>
        </ArticleCardMetaAuthor>
      )}

      {readTime !== null && readTime !== undefined && (
        <ArticleCardMetaReadTime>
          <ClockIcon />
          <ArticleCardMetaValue>
            {intl.formatMessage(
              {
                id: 'component.server.page.article.articleInfoHeader.readTime',
                defaultMessage: '{readTime} min',
              },
              { readTime: <IntlDecimal key="readTime" value={readTime} /> }
            )}
          </ArticleCardMetaValue>
        </ArticleCardMetaReadTime>
      )}
    </ArticleCardMeta>
  );
}
